import { useQuery } from '@tanstack/react-query'
import { StudentDetailRes, StudentStat } from '@/types/student.type'
import { StudentTestRes } from '@/types/test.type'
import { axiosInstance } from '@/lib/axios'

interface StudentProps {
  studentId: string
  enable?: boolean
}

interface StudentStatProps extends StudentProps {
  classId: string
}

interface StudentTestProps {
  classId: string
  studentId: string
  page: number
  isPracTest: string
  search?: string
  creatorType: string
}

export const getStudentDetail = async ({ studentId }: StudentProps) => {
  return axiosInstance.get<StudentDetailRes>(`/user/${studentId}/details`)
}

export const getStudentStat = async ({
  studentId,
  classId,
}: StudentStatProps) => {
  return axiosInstance.get<StudentStat>(
    `/class/${classId}/students/${studentId}/statistics`
  )
}

export const getStudentTests = async ({
  classId,
  studentId,
  page,
  isPracTest,
  search,
  creatorType,
}: StudentTestProps) => {
  const baseUrl = `/class/${classId}/students/${studentId}/practice-tests?page=${page}&limit=10&is_practice=${isPracTest}&search=${search}`
  const url =
    creatorType === 'all' ? baseUrl : `${baseUrl}&creator_type=${creatorType}`

  return axiosInstance.get<StudentTestRes>(url)
}

export const useGetStudentDetail = ({ studentId, enable }: StudentProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['studentDetail', studentId, enable],
    queryFn: () => getStudentDetail({ studentId, enable }),
    enabled: !!studentId && enable,
  })

  return {
    studentDetail: data?.data,
    isLoadingStudentDetail: isLoading,
    refetchStudentDetail: refetch,
  }
}

export const useGetStudentTests = ({
  classId,
  studentId,
  page,
  isPracTest,
  search,
  creatorType,
}: StudentTestProps) => {
  const { data, isLoading, isRefetching, refetch } = useQuery({
    queryKey: [
      'studentTest',
      classId,
      studentId,
      page,
      isPracTest,
      search,
      creatorType,
    ],
    queryFn: () =>
      getStudentTests({
        classId,
        studentId,
        page,
        isPracTest,
        search,
        creatorType,
      }),
    enabled: !!Number(classId) && !!studentId,
  })

  return {
    studentTests: data?.data?.data ?? [],
    isLoadingStudentTest: isLoading || isRefetching,
    meta: data?.data.meta,
    refetchStudentTests: refetch,
  }
}

export const useGetStudentStat = ({
  studentId,
  classId,
  enable,
}: StudentStatProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['studentStat', studentId, classId, enable],
    queryFn: () => getStudentStat({ classId, studentId }),
    enabled: enable && !!Number(classId),
  })

  return {
    studentStat: data?.data,
    isLoadingStudentStat: isLoading,
    refetchStudentStat: refetch,
  }
}
